<script setup>
import router from '@/router'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
import { useDisplay } from 'vuetify'
import { viteConfig } from '@/services/utils'
import * as Auth from '@/services/Auth'
import { useMainStore } from '@/stores/main'
import { RouterLink } from 'vue-router'
import { Config } from '@/Config'
import Logo from './Logo.vue'

const store = useMainStore()

const { locale } = useI18n() 
const { mobile } = useDisplay()
const drawer = ref(false)

const changeLanguage = (lang) => {
  locale.value = lang
}

const handleLogout = async () => {
  await Auth.logout()
  drawer.value = false
  router.push({ name: 'home' })
}

const closeDrawer = () => {
  drawer.value = false
}
</script>

<template>
  <!-- Navigation Drawer for Mobile -->
  <v-navigation-drawer
    v-model="drawer"
    location="left"
    temporary
    v-if="mobile"
  >
    <v-list>
      <!-- Main Navigation Items -->
      <v-list-item 
        prepend-icon="mdi-home" 
        :title="$t('navigation.home')"
        to="/"
        @click="closeDrawer"
      />
      <v-list-item 
        v-if="Auth.isAuthenticated()"
        prepend-icon="mdi-dashboard" 
        :title="$t('navigation.dashboard')"
        to="/dashboard"
        @click="closeDrawer"
      />
      <v-list-item 
        prepend-icon="mdi-information-outline" 
        :title="$t('navigation.info')"
        to="/info"
        @click="closeDrawer"
      />
      
      <v-divider />
      
      <!-- Authentication Section for Mobile -->
      <template v-if="!Auth.isAuthenticated()">
        <v-list-item 
          prepend-icon="mdi-login" 
          :title="$t('common.login')"
          to="/login"
          @click="closeDrawer"
        />
        <v-list-item 
          prepend-icon="mdi-account-plus" 
          :title="$t('common.register')"
          to="/register"
          @click="closeDrawer"
        />
      </template>
      
      <!-- User Menu Items for Mobile when authenticated -->
      <template v-else>
        <v-list-subheader>{{ store.user.name }}</v-list-subheader>
        <v-list-item 
          prepend-icon="mdi-account" 
          :title="$t('navigation.profile')" 
          to="/profile"
          @click="closeDrawer"
        />
        <v-list-item 
          prepend-icon="mdi-cog" 
          :title="$t('navigation.settings')" 
          to="/settings"
          @click="closeDrawer"
        />
        <v-list-item 
          prepend-icon="mdi-logout" 
          :title="$t('common.logout')"
          @click="handleLogout"
        />
      </template>
      
      <template v-if="!Auth.isAuthenticated()">
        <v-divider />
        
        <!-- Language Selection for Mobile -->
        <v-list-subheader>{{ $t('navigation.language') }}</v-list-subheader>
        <v-list-item 
          v-for="(language, key) in Config.languages"
          :key="key"
          prepend-icon="mdi-translate"
          :title="language"
          @click="changeLanguage(key)"
          :class="{ 'v-list-item--active': locale === key }"
        />
      </template>
    </v-list>
  </v-navigation-drawer>





  <v-app-bar density="compact"
    scroll-behavior="fully-hide"
    scroll-threshold="100"
  >
    <!-- Mobile Menu Button -->
    <v-app-bar-nav-icon
      v-if="mobile"
      @click="drawer = !drawer"
    />

    <!-- Logo -->
    <RouterLink v-else
      to="/"
      class="px-4 text-decoration-none text-high-emphasis"
      :title="viteConfig('APP_NAME')"
    >
      <Logo />
    </RouterLink>
    
    <!-- Desktop Navigation -->
    <template v-if="!mobile">
      <v-btn to="/" variant="text">{{ $t('navigation.home') }}</v-btn>
      <v-btn v-if="Auth.isAuthenticated()" to="/dashboard" variant="text">{{ $t('navigation.dashboard') }}</v-btn>
      <v-btn to="/info" variant="text">{{ $t('navigation.info') }}</v-btn>
    </template>
    
    <!-- App Title for Mobile -->
    <v-toolbar-title v-if="mobile" class="ml-2">
      <RouterLink to="/" class="text-decoration-none text-high-emphasis" :title="viteConfig('APP_NAME')">
        <Logo />
      </RouterLink>
    </v-toolbar-title>
    
    <v-spacer></v-spacer>

    <!-- Desktop Authentication Section -->
    <template v-if="!mobile">
      <div v-if="!Auth.isAuthenticated()" class="d-flex align-center ga-2 mr-4">
        <v-btn 
          to="/login"
          variant="text"
          size="small"
          prepend-icon="mdi-login"
        >
          {{ $t('common.login') }}
        </v-btn>
        <v-btn 
          to="/register"
          variant="outlined"
          size="small"
          color="primary"
          prepend-icon="mdi-account-plus"
        >
          {{ $t('common.register') }}
        </v-btn>
      </div>

      <!-- User Menu when authenticated (Desktop) -->
      <div v-else class="d-flex align-center ga-2 mr-4">
        <v-menu location="bottom end">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              prepend-icon="mdi-account-circle"
              append-icon="mdi-chevron-down"
            >
              {{ store.user.name }}
            </v-btn>
          </template>
          <v-list>
            <v-list-item 
              prepend-icon="mdi-account" 
              :title="$t('navigation.profile')" 
              to="/profile"
            />
            <v-list-item 
              prepend-icon="mdi-cog" 
              :title="$t('navigation.settings')" 
              to="/settings"
            />
            <v-divider />
            <v-list-item 
              prepend-icon="mdi-logout" 
              :title="$t('common.logout')"
              @click="handleLogout"
            />
          </v-list>
        </v-menu>
      </div>
      
      <!-- Language Selector (Desktop) -->
      <v-btn-group v-if="!Auth.isAuthenticated()">
        <v-btn 
          v-for="(language, key) in Config.languages"
          :key="key"
          @click="changeLanguage(key)" 
          :variant="locale === key ? 'tonal' : 'text'"
          size="small"
        >
          <span class="text-uppercase">{{ key }}</span>
        </v-btn>
      </v-btn-group>
    </template>
    
    <!-- Mobile Language Quick Switch -->
    <template v-else-if="!Auth.isAuthenticated()">
      <v-menu location="bottom end">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            size="small"
            icon="mdi-translate"
          />
        </template>
        <v-list>
          <v-list-item 
            v-for="(language, key) in Config.languages"
            :key="key"
            :title="language"
            @click="changeLanguage(key)"
            :class="{ 'v-list-item--active': locale === key }"
          />
        </v-list>
      </v-menu>
    </template>
  </v-app-bar>
</template>